/** Recent searches belong to the current browser, not a customer account. */
class SSearchHistory extends HTMLElement {
  #abort;
  #form;
  #input;
  #panel;
  #list;
  #media;
  #announcementFrame;
  #storageKey = 'sahla-recent-searches-v1';

  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#input = this.querySelector('input[name="q"]');
    this.#panel = this.querySelector('[data-search-history-panel]');
    this.#list = this.querySelector('[data-search-history-list]');
    const clear = this.querySelector('[data-search-history-clear]');
    if (!this.#form || !this.#input || !this.#panel || !this.#list || !clear) return;

    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.#media = window.matchMedia('(min-width: 768px)');

    this.#input.addEventListener('focus', () => this.#open(), options);
    this.#input.addEventListener('input', () => {
      if (this.#input.value.trim()) this.#close();
      else this.#open();
    }, options);
    this.#form.addEventListener('submit', () => {
      // s-live-form submits programmatically while typing in the dialog; those aren't searches.
      if (this.dataset.historyMode !== 'mobile-dialog' && this.#active()) this.#save(this.#input.value);
    }, options);
    this.#input.addEventListener('keydown', (event) => {
      if (this.dataset.historyMode === 'mobile-dialog' && this.#active() && event.key === 'Enter' && !event.defaultPrevented) {
        this.#save(this.#input.value);
      }
    }, options);
    clear.addEventListener('click', () => {
      try { window.localStorage.removeItem(this.#storageKey); } catch { /* Private storage may be unavailable. */ }
      this.#close();
      this.#input.focus();
      const announcer = this.ownerDocument.getElementById('sr-announcer');
      if (announcer) {
        announcer.textContent = '';
        cancelAnimationFrame(this.#announcementFrame);
        this.#announcementFrame = requestAnimationFrame(() => {
          announcer.textContent = this.dataset.clearedMessage || '';
        });
      }
    }, options);
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || this.#panel.hidden) return;
      event.preventDefault();
      event.stopPropagation();
      this.#close();
      this.#input.focus();
    }, options);
    this.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (this.isConnected && !this.contains(this.ownerDocument.activeElement)) this.#close();
      });
    }, options);
    this.ownerDocument.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.#close();
    }, options);
    this.#media.addEventListener('change', () => this.#close(), options);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#announcementFrame);
  }

  #read() {
    try {
      const terms = JSON.parse(window.localStorage.getItem(this.#storageKey) || '[]');
      return Array.isArray(terms) ? terms.filter((term) => typeof term === 'string' && term.trim()).slice(0, 5) : [];
    } catch {
      return [];
    }
  }

  #save(value) {
    const term = value.trim();
    if (!term) return;
    const terms = [term, ...this.#read().filter((previous) => previous.toLocaleLowerCase() !== term.toLocaleLowerCase())].slice(0, 5);
    try { window.localStorage.setItem(this.#storageKey, JSON.stringify(terms)); } catch { /* Search still works without storage. */ }
  }

  #active() {
    if (this.dataset.historyMode === 'mobile-dialog') return !this.#media.matches;
    // The homepage header form is hidden on mobile; other pages expose it directly.
    return this.#form.getClientRects().length > 0;
  }

  #open() {
    if (!this.#active()) return;
    const terms = this.#read();
    if (!terms.length) {
      this.#close();
      return;
    }
    const items = this.ownerDocument.createDocumentFragment();
    for (const term of terms) {
      const item = this.ownerDocument.createElement('li');
      const link = this.ownerDocument.createElement('a');
      const destination = new URL(this.#form.action, this.ownerDocument.baseURI);
      destination.searchParams.set('q', term);
      link.href = destination.toString();
      link.textContent = term;
      link.className = 'block min-h-touch-target truncate rounded-button px-3 py-3 text-sm text-on-surface no-underline hover:bg-surface-secondary focus-visible:outline-2 focus-visible:outline-accent';
      link.addEventListener('click', () => this.#save(term), { signal: this.#abort.signal });
      item.append(link);
      items.append(item);
    }
    this.#list.replaceChildren(items);
    this.#panel.hidden = false;
  }

  #close() {
    this.#panel.hidden = true;
  }
}

customElements.define('s-search-history', SSearchHistory);
